import { message } from "antd";
import React, { useEffect, useState } from "react";
import { Card, Table, Button, Space, Popconfirm, Modal } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import routes from "../../config/routes/routes";
import axios from "axios";
import dayjs from "dayjs";
import AntdModal from "../../components/common/AntdModal";
import AppButton from "../../components/common/AppButton";
import AppInput from "../../components/common/AppInput";
import { Form } from "antd";

const calculateEaster = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return dayjs(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
};

const generateCommonHolidays = (year) => {
  const easter = calculateEaster(year);
  const goodFriday = easter.subtract(2, 'day');
  const easterMonday = easter.add(1, 'day');
  
  return [
    { name: "New Year's Day", date: `${year}-01-01`, description: "New Year celebration" },
    { name: "Good Friday", date: goodFriday.format("YYYY-MM-DD"), description: "Christian holiday" },
    { name: "Easter Monday", date: easterMonday.format("YYYY-MM-DD"), description: "Christian holiday" },
    { name: "Labour Day", date: `${year}-05-01`, description: "International Workers' Day" },
    { name: "Independence Day", date: `${year}-10-09`, description: "Uganda Independence Day" },
    { name: "Christmas Day", date: `${year}-12-25`, description: "Christmas celebration" },
    { name: "Boxing Day", date: `${year}-12-26`, description: "Day after Christmas" },
    { name: "Martyrs Day", date: `${year}-06-03`, description: "Uganda Martyrs Day" },
  ];
};

const PublicHolidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();

  useEffect(() => {
    document.title = routes.settings.publicHolidays.title;
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/settings/public-holidays");
      let holidaysData = [];
      if (Array.isArray(res)) {
        holidaysData = res;
      } else if (Array.isArray(res?.data)) {
        holidaysData = res.data;
      } else if (res?.data?.data && Array.isArray(res.data.data)) {
        holidaysData = res.data.data;
      } else if (res?.data && typeof res.data === 'object') {
        holidaysData = res.data.data || [];
      }
      setHolidays(holidaysData);
    } catch (error) {
      message.error("Failed to fetch public holidays");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        name: values.name,
        date: values.date.format("YYYY-MM-DD"),
        description: values.description || "",
      };

      if (editingHoliday) {
        await axios.put(`/settings/public-holidays/${editingHoliday.id}`, payload);
        message.success("Public holiday updated successfully");
      } else {
        await axios.post("/settings/public-holidays", payload);
        message.success("Public holiday created successfully");
      }

      setShowModal(false);
      setEditingHoliday(null);
      form.resetFields();
      fetchHolidays();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to save public holiday";
      message.error(errorMsg);
    }
  };

  const handleEdit = (record) => {
    setEditingHoliday(record);
    form.setFieldsValue({
      name: record.name,
      date: dayjs(record.date),
      description: record.description || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/settings/public-holidays/${id}`);
      message.success("Public holiday deleted successfully");
      fetchHolidays();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete public holiday";
      message.error(errorMsg);
    }
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    form.resetFields();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    form.resetFields();
  };

  const handleImportCommonHolidays = async (values) => {
    const year = values.year.year();
    setImporting(true);
    try {
      const commonHolidays = generateCommonHolidays(year);
      const existingHolidayNames = holidays.map(h => h.name.toLowerCase());
      
      let imported = 0;
      let skipped = 0;
      
      for (const holiday of commonHolidays) {
        const exists = existingHolidayNames.includes(holiday.name.toLowerCase()) ||
          holidays.some(h => h.name.toLowerCase() === holiday.name.toLowerCase() && h.date === holiday.date);
        
        if (!exists) {
          try {
            await axios.post("/settings/public-holidays", holiday, {
              skipNotification: true
            });
            imported++;
          } catch (error) {
            skipped++;
          }
        } else {
          skipped++;
        }
      }
      
      if (imported > 0 && skipped > 0) {
        message.success(`Successfully imported ${imported} holiday(s) for ${year}. ${skipped} holiday(s) were skipped (already exist).`);
      } else if (imported > 0) {
        message.success(`Successfully imported ${imported} common holiday(s) for ${year}`);
      } else if (skipped > 0) {
        message.warning(`All ${skipped} common holiday(s) for ${year} already exist`);
      }
      
      setShowImportModal(false);
      importForm.resetFields();
      fetchHolidays();
    } catch (error) {
      message.error("Failed to import common holidays");
    } finally {
      setImporting(false);
    }
  };

  const openImportModal = () => {
    importForm.setFieldsValue({
      year: dayjs(),
    });
    setShowImportModal(true);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 150,
      render: (date) => dayjs(date).format("DD MMM YYYY"),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this public holiday?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="container-fluid p-2">
      <Card
        title={<h5 className="fw-bold text-uppercase mb-0">Public Holidays</h5>}
        extra={
          <Space>
            <AppButton
              text="Import Common Holidays"
              iconBefore={<DownloadOutlined />}
              onClick={openImportModal}
            />
            <AppButton
              text="Add Public Holiday"
              iconBefore={<PlusOutlined />}
              onClick={openAddModal}
            />
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={holidays}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <AntdModal
        title={editingHoliday ? "Edit Public Holiday" : "Add Public Holiday"}
        open={showModal}
        closeModal={closeModal}
        footer={[
          <AppButton
            key="cancel"
            text="Cancel"
            onClick={closeModal}
            danger
          />,
          <AppButton
            key="submit"
            text={editingHoliday ? "Update" : "Create"}
            onClick={() => form.submit()}
            type="primary"
          />,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <AppInput
            label="Holiday Name"
            name="name"
            rules={[{ required: true, message: "Holiday name is required" }]}
            inputAttributes={{
              placeholder: "Enter holiday name",
            }}
          />

          <AppInput
            label="Date"
            name="date"
            type="date"
            rules={[{ required: true, message: "Date is required" }]}
            inputAttributes={{
              placeholder: "Select date",
            }}
          />

          <AppInput
            label="Description"
            name="description"
            type="textarea"
            inputAttributes={{
              placeholder: "Enter description (optional)",
              rows: 3,
            }}
          />
        </Form>
      </AntdModal>

      <Modal
        title="Import Common Holidays"
        open={showImportModal}
        onCancel={() => {
          setShowImportModal(false);
          importForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowImportModal(false);
              importForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="import"
            type="primary"
            loading={importing}
            onClick={() => importForm.submit()}
          >
            Import
          </Button>,
        ]}
      >
        <Form
          form={importForm}
          layout="vertical"
          onFinish={handleImportCommonHolidays}
        >
          <AppInput
            label="Select Year"
            name="year"
            type="date"
            rules={[{ required: true, message: "Year is required" }]}
            inputAttributes={{
              picker: "year",
              placeholder: "Select year",
            }}
          />
          <p className="text-muted mt-2">
            This will import common holidays including: New Year's Day, Good Friday, 
            Easter Monday, Labour Day, Independence Day, Christmas Day, Boxing Day, and Martyrs Day.
            Holidays that already exist will be skipped.
          </p>
        </Form>
      </Modal>
    </div>
  );
};

export default PublicHolidays;
